/** TanStack Query hooks for admin settings and dashboard. */

import { useQuery, useMutation, useQueryClient } from "@tanstack/react-query";
import { api } from "@/services/api";
import type { Settings, Job } from "@/types";

// --- Settings ---

export function useAdminSettings() {
  return useQuery<Settings>({
    queryKey: ["admin-settings"],
    queryFn: () => api.get("/admin/reporting/settings"),
  });
}

export function useUpdateSettings() {
  const qc = useQueryClient();
  return useMutation<Settings, Error, Partial<Settings>>({
    mutationFn: (body) => api.put("/admin/reporting/settings", body),
    onSuccess: () => qc.invalidateQueries({ queryKey: ["admin-settings"] }),
  });
}

export function useFetchGeminiModels() {
  return useQuery<{ models: { value: string; label: string; detail: string }[] }>({
    queryKey: ["gemini-models-available"],
    queryFn: () => api.get("/admin/reporting/gemini-models"),
    enabled: false, // Manual trigger via refetch
  });
}

// --- Dashboard ---

interface DashboardData {
  recentJobs: Job[];
  topPlugins: string[];
  systemStats?: {
    activePlugins: number;
    pendingJobs: number;
    failedJobs: number;
    activeSchedules: number;
  };
}

export function useDashboard() {
  return useQuery<DashboardData>({
    queryKey: ["dashboard"],
    queryFn: () => api.get("/reporting/dashboard"),
  });
}
